import React, { Component, Fragment } from 'react';
import { bool, string } from 'prop-types';
import { compose } from 'redux';
import { map, path } from 'ramda';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { Field, Form as FinalForm } from 'react-final-form';
import isEqual from 'lodash/isEqual';
import classNames from 'classnames';

import config from '../../config';
import { ensureCurrentUser } from '../../util/data';
import { propTypes } from '../../util/types';
import * as validators from '../../util/validators';
import { isUploadImageOverLimitError } from '../../util/errors';
import {
  Form,
  Avatar,
  ImageFromFile,
  IconSpinner,
  FieldTextInput,
  PrimaryButton,
  UploadFileField,
  FieldSelect,
} from '../../components';

import css from './ProfileSettingsForm.css';
import ContactDetails from '../../components/ContactDetails';
import HowDidYouKnowUsSelectField from '../../components/HowDidYouKnowUsSelectField';

const ACCEPT_IMAGES = 'image/*';
const UPLOAD_CHANGE_DELAY = 2000; // Show spinner so that browser has time to load img srcset
const acceptableIdentityDocuments = config.custom.stripeIdentityDocuments;
const acceptableAddressDocuments = config.custom.stripeAddressDocuments;

export const getCniFormData = (cniDocument) => {
  if (!cniDocument) return null;

  const formData = new FormData();
  formData.append('file', cniDocument);
  formData.append('purpose', 'identity_document');
  return formData;
};

class ProfileSettingsFormComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      uploadDelay: false,
      hasEnteredIdentityDocument: false,
      hasEnteredProofOfAddressDocument: false,
    };
    this.uploadDelayTimeoutId = null;
    this.submittedValues = {};
  }

  componentWillReceiveProps(nextProps) {
    // Upload delay is additional time window where Avatar is added to the DOM,
    // but not yet visible (time to load image URL from srcset)
    if (this.props.uploadInProgress && !nextProps.uploadInProgress) {
      this.setState({ uploadDelay: true });
      this.uploadDelayTimeoutId = window.setTimeout(() => {
        this.setState({ uploadDelay: false });
      }, UPLOAD_CHANGE_DELAY);
    }
  }

  componentWillUnmount() {
    window.clearTimeout(this.blurTimeoutId);
  }

  getCNIDocumentName = () => {
    const { cniFormData } = this.state;
    if (!cniFormData) return ;

    const file = cniFormData.get('file');
    return path(['name'], file);
  };

  handleChangeDocumentCNI = (file) => {
    if (!file) return;

    const fileSize = ((file.size / 1024) / 1024).toFixed(4);
    // Block Size to 5MB
    if (fileSize > 5) return;

    const { setCNIDocument } = this.props;
    setCNIDocument(getCniFormData(file));
    this.setState({ hasEnteredIdentityDocument: true });
  };

  handleChangeDocumentProofOfAddress = (file) => {
    if (!file) return;

    const fileSize = ((file.size / 1024) / 1024).toFixed(4);
    // Block Size to 5MB
    if (fileSize > 5) return;

    const { setProofOfAddressDocument } = this.props;
    setProofOfAddressDocument(getCniFormData(file));
    this.setState({ hasEnteredProofOfAddressDocument: true });
  };

  render() {
    return (
      <FinalForm
        {...this.props}
        render={fieldRenderProps => {
          const {
            className,
            currentUser,
            handleSubmit,
            intl,
            invalid,
            onImageUpload,
            pristine,
            profileImage,
            rootClassName,
            updateInProgress,
            updateProfileError,
            uploadImageError,
            uploadInProgress,
            form,
            values,
            canShowCNIField,
            canShowProofOfAddress,
          } = fieldRenderProps;

          const user = ensureCurrentUser(currentUser);

          // First name
          const firstNameLabel = intl.formatMessage({
            id: 'ProfileSettingsForm.firstNameLabel',
          });
          const firstNamePlaceholder = intl.formatMessage({
            id: 'ProfileSettingsForm.firstNamePlaceholder',
          });
          const firstNameRequiredMessage = intl.formatMessage({
            id: 'ProfileSettingsForm.firstNameRequired',
          });
          const firstNameRequired = validators.required(firstNameRequiredMessage);

          // Last name
          const lastNameLabel = intl.formatMessage({
            id: 'ProfileSettingsForm.lastNameLabel',
          });
          const lastNamePlaceholder = intl.formatMessage({
            id: 'ProfileSettingsForm.lastNamePlaceholder',
          });
          const lastNameRequiredMessage = intl.formatMessage({
            id: 'ProfileSettingsForm.lastNameRequired',
          });
          const lastNameRequired = validators.required(lastNameRequiredMessage);

          // Bio
          const bioLabel = intl.formatMessage({
            id: 'ProfileSettingsForm.bioLabel',
          });
          const bioPlaceholder = intl.formatMessage({
            id: 'ProfileSettingsForm.bioPlaceholder',
          });

          const uploadingOverlay =
            uploadInProgress || this.state.uploadDelay ? (
              <div className={css.uploadingImageOverlay}>
                <IconSpinner />
              </div>
            ) : null;

          const hasUploadError = !!uploadImageError && !uploadInProgress;
          const errorClasses = classNames({ [css.avatarUploadError]: hasUploadError });
          const transientUserProfileImage = profileImage.uploadedImage || user.profileImage;
          const transientUser = { ...user, profileImage: transientUserProfileImage };
          const hasHowDidYouKnowUs = this.props.initialValues.howDidYouKnowUs ? true : false;

          // Ensure that file exists if imageFromFile is used
          const fileExists = !!profileImage.file;
          const fileUploadInProgress = uploadInProgress && fileExists;
          const delayAfterUpload = profileImage.imageId && this.state.uploadDelay;
          const imageFromFile =
            fileExists && (fileUploadInProgress || delayAfterUpload) ? (
              <ImageFromFile
                id={profileImage.id}
                className={errorClasses}
                rootClassName={css.uploadingImage}
                aspectRatioClassName={css.squareAspectRatio}
                file={profileImage.file}
              >
                {uploadingOverlay}
              </ImageFromFile>
            ) : null;

          // Avatar is rendered in hidden during the upload delay
          // Upload delay smoothes image change process:
          // responsive img has time to load srcset stuff before it is shown to user.
          const avatarClasses = classNames(errorClasses, css.avatar, {
            [css.avatarInvisible]: this.state.uploadDelay,
          });
          const avatarComponent =
            !fileUploadInProgress && profileImage.imageId ? (
              <Avatar
                className={avatarClasses}
                renderSizes="(max-width: 767px) 96px, 240px"
                user={transientUser}
                disableProfileLink
              />
            ) : null;

          const chooseAvatarLabel =
            profileImage.imageId || fileUploadInProgress ? (
              <div className={css.avatarContainer}>
                {imageFromFile}
                {avatarComponent}
                <div className={css.changeAvatar}>
                  <FormattedMessage id="ProfileSettingsForm.changeAvatar" />
                </div>
              </div>
            ) : (
              <div className={css.avatarPlaceholder}>
                <div className={css.avatarPlaceholderText}>
                  <FormattedMessage id="ProfileSettingsForm.addYourProfilePicture" />
                </div>
                <div className={css.avatarPlaceholderTextMobile}>
                  <FormattedMessage id="ProfileSettingsForm.addYourProfilePictureMobile" />
                </div>
              </div>
            );

          const submitError = updateProfileError ? (
            <div className={css.error}>
              <FormattedMessage id="ProfileSettingsForm.updateProfileFailed" />
            </div>
          ) : null;
          const classes = classNames(rootClassName || css.root, className);
          const submitInProgress = updateInProgress;
          const submittedOnce = Object.keys(this.submittedValues).length > 0;
          const { hasEnteredIdentityDocument, hasEnteredProofOfAddressDocument } = this.state;
          const hasEnteredDocuments = hasEnteredIdentityDocument && hasEnteredProofOfAddressDocument;
          const submitErrorDueToInvalidForm = submittedOnce && (!pristine || hasEnteredDocuments) && invalid && (
            <div className={css.error}>
              <FormattedMessage id="ProfileSettingsForm.updateProfileFailedDueToInvalidForm" />
            </div>
          );
          const pristineSinceLastSubmit = !invalid && !updateProfileError && submittedOnce && isEqual(values, this.submittedValues);
          const submitDisabled =  (pristine && !hasEnteredDocuments) ||
            pristineSinceLastSubmit || uploadInProgress || submitInProgress;
          const birthdayLabel = intl.formatMessage({ id: 'ContactDetailsForm.birthdayLabel' });
          const birthdayRequiredMessage = intl.formatMessage({ id: 'ContactDetailsForm.birthdayRequired' });
          const birthdayRequired = validators.required(birthdayRequiredMessage);
          const birthdayMinAge = validators.ageAtLeastSwimmy(
            intl.formatMessage(
              {
                id: 'ProfileSettingsForm.birthdayMinAge',
              },
              {
                minAge: config.custom.marketplaceAgeRequirement,
              }
            ),
            config.custom.marketplaceAgeRequirement
          );

          const uploadCNILabel = intl.formatMessage({
            id: 'UploadCNIField.label',
          });
          const uploadCNIWarningContent = intl.formatMessage({
            id: 'UploadCNIField.warningContent',
          });

          const uploadProofOfAddressLabel = intl.formatMessage({
            id: 'UploadProofOfAddressField.label',
          });
          const uploadProofOfAddressWarningContent = intl.formatMessage({
            id: 'UploadProofOfAddressField.warningContent',
          });
          const uploadDocumentsAlertContent = intl.formatMessage({
            id: 'UploadDocumentsAlert.content',
          });
          const uploadDocumentsAlertWord = intl.formatMessage({
            id: 'UploadDocumentsAlert.word',
          });
          const languageRequiredMessage = intl.formatMessage({ id: 'ProfileSettingsForm.languageRequiredMessage' });
          const languagePlaceholder = intl.formatMessage({ id: 'ProfileSettingsForm.languagePlaceholder' });
          const languageOptionFr = intl.formatMessage({ id: 'ProfileSettingsForm.languageOptionFr' });
          const languageOptionEn = intl.formatMessage({ id: 'ProfileSettingsForm.languageOptionEn' });
          const languageOptionEs = intl.formatMessage({ id: 'ProfileSettingsForm.languageOptionEs' });
          const languageOptionDe = intl.formatMessage({ id: 'ProfileSettingsForm.languageOptionDe' });
          const languageOptionIt = intl.formatMessage({ id: 'ProfileSettingsForm.languageOptionIt' });
          const languageOptionFrBe = intl.formatMessage({ id: 'ProfileSettingsForm.languageOptionFrBe' });
          const languageLabel = intl.formatMessage({ id: 'ProfileSettingsForm.languageLabel' });
          
          const acceptableDocumentsHeading = intl.formatMessage({ id: 'PayoutDetailsForm.acceptableDocumentsHeading' });
          const warningWord = intl.formatMessage({
            id: 'UploadCNIField.warning',
          });
          const warningContent = (acceptableDocuments, warningMessage) => (
            <Fragment>
              <span>{acceptableDocumentsHeading}</span>
              <ul className={css.list}>
                {map((item) => (<li>{item}</li>),acceptableDocuments)}
              </ul>
              <span><b>{warningWord}</b>, {warningMessage}</span>
            </Fragment>
          );
          
          return (
            <Form
              className={classes}
              onSubmit={e => {
                this.submittedValues = values;
                console.log('e', e);
                handleSubmit(e);
              }}
            >
              <div className={css.sectionContainer}>
                <h2 className={css.sectionTitle}>
                  <FormattedMessage id="ProfileSettingsForm.yourProfilePicture" />
                </h2>
                <div className={css.sectionContent}>
                  <Field
                    accept={ACCEPT_IMAGES}
                    id="profileImage"
                    name="profileImage"
                    label={chooseAvatarLabel}
                    type="file"
                    form={null}
                    uploadImageError={uploadImageError}
                    disabled={uploadInProgress}
                  >
                    {fieldProps => {
                      const {
                        accept,
                        id,
                        input,
                        label,
                        type,
                        disabled,
                        uploadImageError,
                      } = fieldProps;
                      const { name } = input;
                      const onChange = e => {
                        const file = e.target.files[0];
                        form.change(`profileImage`, file);
                        form.blur(`profileImage`);
                        if (file != null) {
                          const tempId = `${file.name}_${Date.now()}`;
                          onImageUpload({ id: tempId, file });
                        }
                      };

                      let error = null;

                      if (isUploadImageOverLimitError(uploadImageError)) {
                        error = (
                          <div className={css.error}>
                            <FormattedMessage id="ProfileSettingsForm.imageUploadFailedFileTooLarge" />
                          </div>
                        );
                      } else if (uploadImageError) {
                        error = (
                          <div className={css.error}>
                            <FormattedMessage id="ProfileSettingsForm.imageUploadFailed" />
                          </div>
                        );
                      }

                      return (
                        <div className={css.uploadAvatarWrapper}>
                          <label className={css.label} htmlFor={id}>
                            {label}
                          </label>
                          <input
                            accept={accept}
                            id={id}
                            name={name}
                            className={css.uploadAvatarInput}
                            disabled={disabled}
                            onChange={onChange}
                            type={type}
                          />
                          <div className={css.fileInfo}>
                              <FormattedMessage id="ProfileSettingsForm.fileInfo" /><br/>
                              <FormattedMessage id="ProfileSettingsForm.fileSize" />
                          </div>
                          {error}
                        </div>
                      );
                    }}
                  </Field>
                  <div className={css.avatarHelper}>
                    <FormattedMessage id="ProfileSettingsForm.avatarHelper"/>
                  </div>
                </div>
              </div>
              <div className={css.sectionContainer}>
                <h2 className={css.sectionTitle}>
                  <FormattedMessage id="ProfileSettingsForm.personalInformation" />
                </h2>
                <div className={css.sectionContent}>
                  <div className={css.nameContainer}>
                    <FieldTextInput
                      className={css.firstName}
                      type="text"
                      id="firstName"
                      name="firstName"
                      label={firstNameLabel}
                      placeholder={firstNamePlaceholder}
                      validate={firstNameRequired}
                    />
                    <FieldTextInput
                      className={css.lastName}
                      type="text"
                      id="lastName"
                      name="lastName"
                      label={lastNameLabel}
                      placeholder={lastNamePlaceholder}
                      validate={lastNameRequired}
                    />
                  </div>
                  <FieldTextInput
                    className={css.field}
                    name="birthdayDate"
                    id={'birthdayDate'}
                    label={birthdayLabel}
                    type="date"
                    validate={validators.composeValidators(birthdayRequired, birthdayMinAge)}
                  />
                  <p className={css.helper}>
                    <FormattedMessage id="ContactDetailsForm.privateBirthdateHelper"/>
                  </p>
                  {(canShowCNIField || canShowProofOfAddress) && (
                    <div className={css.cautionMessage}>
                      <strong>{uploadDocumentsAlertWord}</strong> {uploadDocumentsAlertContent}
                  </div>
                  )}
                  {canShowCNIField && (
                    <UploadFileField
                      className={css.uploadField}
                      name="documentCNI"
                      label={uploadCNILabel}
                      fileDescription="UploadCNIField.choosePhoto"
                      warningContent={warningContent(acceptableIdentityDocuments, uploadCNIWarningContent)}
                      handleChangeDocumentFile={this.handleChangeDocumentCNI}
                      getFileDocumentName={this.getCNIDocumentName}
                      acceptedFormat=".jpg, .jpeg, .png"
                    />
                    )
                  }
                  {canShowProofOfAddress && (
                      <UploadFileField
                        name="documentProofOfAddress"
                        label={uploadProofOfAddressLabel}
                        fileDescription="UploadProofOfAddressField.choosePhoto"
                        warningContent={warningContent(acceptableAddressDocuments, uploadProofOfAddressWarningContent)}
                        handleChangeDocumentFile={this.handleChangeDocumentProofOfAddress}
                        getFileDocumentName={this.getCNIDocumentName}
                        acceptedFormat=".jpg, .jpeg, .png, .pdf"
                      />
                    )
                  }
                  {!hasHowDidYouKnowUs && (
                    <div>
                      <HowDidYouKnowUsSelectField
                        className={css.field}
                        id={'howDidYouKnowUs'}
                        name="howDidYouKnowUs"
                        intl={intl}
                      />
                      <p className={css.helper}>
                        <FormattedMessage id="ContactDetailsForm.privateDataHelper"/>
                      </p>
                    </div>
                  )}
                  <FieldSelect
                    className={css.field}
                    id="language"
                    name="language"
                    label={languageLabel}
                    validate={validators.required(languageRequiredMessage)}
                  >
                    <option value="">{languagePlaceholder}</option>
                    <option value="fr">{languageOptionFr}</option>
                    <option value="fr-BE">{languageOptionFrBe}</option>
                    <option value="en">{languageOptionEn}</option>
                    <option value="es">{languageOptionEs}</option>
                    <option value="de">{languageOptionDe}</option>
                    <option value="it">{languageOptionIt}</option>
                  </FieldSelect>
                  <p className={css.helper}>
                    <FormattedMessage id="ProfileSettingsForm.languageHelper"/>
                  </p>
                </div>
              </div>
              <div className={css.sectionContainer}>
                <h2 className={css.sectionTitle}>
                  <FormattedMessage id="ProfileSettingsForm.contact" />
                </h2>
                <div className={css.sectionContent}>
                  <div className={css.nameContainer}>
                    <ContactDetails
                      phoneNumber={values.phoneNumber}
                    />
                  </div>
                </div>
              </div>
              <div className={classNames(css.sectionContainer, css.lastSection)}>
                <h2 className={css.sectionTitle}>
                  <FormattedMessage id="ProfileSettingsForm.bioHeading" />
                </h2>
                <div className={css.sectionContent}>
                  <FieldTextInput
                    type="textarea"
                    id="bio"
                    name="bio"
                    label={bioLabel}
                    placeholder={bioPlaceholder}
                  />
                  <p className={css.bioInfo}>
                    <FormattedMessage id="ProfileSettingsForm.bioInfo" />
                  </p>
                </div>
              </div>
              {submitError}
              {submitErrorDueToInvalidForm}
              <PrimaryButton
                type="submit"
                inProgress={submitInProgress}
                disabled={submitDisabled}
                ready={pristineSinceLastSubmit}
              >
                <FormattedMessage id="ProfileSettingsForm.saveChanges" />
              </PrimaryButton>
            </Form>
          );
        }}
      />
    );
  }
}

ProfileSettingsFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  uploadImageError: null,
  updateProfileError: null,
  updateProfileReady: false,
  displayCNIField: false,
};

ProfileSettingsFormComponent.propTypes = {
  rootClassName: string,
  className: string,

  uploadImageError: propTypes.error,
  uploadInProgress: bool.isRequired,
  updateInProgress: bool.isRequired,
  updateProfileError: propTypes.error,
  updateProfileReady: bool,
  displayCNIField: bool,

  // from injectIntl
  intl: intlShape.isRequired,
};

const ProfileSettingsForm = compose(injectIntl)(ProfileSettingsFormComponent);

ProfileSettingsForm.displayName = 'ProfileSettingsForm';

export default ProfileSettingsForm;



// WEBPACK FOOTER //
// ./src/forms/ProfileSettingsForm/ProfileSettingsForm.js